import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import PublicLayout from '../../components/layout/PublicLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { Section } from './legalContent';

// The shell for the public legal pages.
//
// Deliberately plain: these are read by somebody who is not signed in — an
// app reviewer, a driver deciding whether to accept an invitation, a
// contractor's lawyer — and the only job is to be legible. One column at a
// reading measure, real headings, no marketing.
//
// The header and footer come from PublicLayout, so this page belongs to the
// same site as the one that sent you here. It used to carry its own nav
// with a different set of links and a "back to the site" line in the
// footer, which is how a reviewer ends up unsure whose policy they are
// reading.

// A placeholder that has not been filled in is rendered so it cannot be
// missed. Publishing "governed by the laws of [[STATE]]" quietly would be
// worse than not publishing at all.
const Placeholder: React.FC<{ text: string }> = ({ text }) => (
  <Box
    component="mark"
    sx={{
      bgcolor: 'warning.light', color: 'warning.dark',
      fontWeight: 700, px: 0.75, borderRadius: 1,
    }}
  >
    {text}
  </Box>
);

const withPlaceholders = (text: string): React.ReactNode => {
  const parts = text.split(/(\[\[[^\]]+\]\])/g);
  return parts.map((part, index) => (
    part.startsWith('[[')
      ? <Placeholder key={index} text={part.replace(/[[\]]/g, '')} />
      : <React.Fragment key={index}>{part}</React.Fragment>
  ));
};

interface Props {
  title: string;
  intro: string;
  lastUpdated: string;
  sections: Section[];
}

const LegalPage: React.FC<Props> = ({ title, intro, lastUpdated, sections }) => {
  useDocumentTitle(title);
  return (
  <PublicLayout>
    <Typography variant="h1" sx={{ mb: 1 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">Last updated {lastUpdated}</Typography>
    <Typography variant="body1" sx={{ mt: 3, fontSize: 17, lineHeight: 1.65 }}>
      {withPlaceholders(intro)}
    </Typography>

    <Divider sx={{ my: 5 }} />

    <Stack gap={5}>
      {sections.map((section) => (
        <Box key={section.heading} component="section">
          <Typography variant="h4" sx={{ mb: 1.5 }}>{section.heading}</Typography>
          {section.body?.map((paragraph, index) => (
            <Typography
              key={index}
              variant="body1"
              sx={{ mb: 1.5, lineHeight: 1.7, color: 'text.primary' }}
            >
              {withPlaceholders(paragraph)}
            </Typography>
          ))}
          {section.bullets && (
            <Stack component="ul" gap={1} sx={{ pl: 3, m: 0, mt: 1 }}>
              {section.bullets.map((bullet, index) => (
                <Typography key={index} component="li" variant="body1" sx={{ lineHeight: 1.7 }}>
                  {bullet.term && <Box component="strong">{bullet.term}. </Box>}
                  {withPlaceholders(bullet.text)}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
      ))}
    </Stack>

  </PublicLayout>
  );
};

export default LegalPage;